/**
 * Filtros e ordenacao locais da biblioteca de modelos.
 *
 * A resposta da API traz o escopo inteiro (empresa + periodo) e TODO recorte
 * de tela -- aba, busca, categoria, setor, status, publico, uso -- acontece
 * aqui, em memoria. Pensado para centenas de modelos: um passo linear por
 * filtro, busca normalizada uma vez, e a pagina nunca renderiza mais que o
 * "ver mais" pediu.
 *
 * Consequencias deliberadas:
 *  1. As contagens dos chips sao FACETADAS: o numero ao lado de "Marketing" e
 *     quantos modelos sobrariam se a pessoa clicasse nele, dados os OUTROS
 *     filtros. Por isso `aplicarFiltros` aceita um filtro a ignorar.
 *  2. Os modelos criados, editados e arquivados na previa (estado local) passam
 *     pelo MESMO predicado que os vindos da API, inclusive o de escopo de
 *     empresa. Sem isso, um modelo criado para a FitPro apareceria na aba da
 *     CodeLab ate o proximo fetch.
 *  3. A ordenacao e sempre total (desempate pelo nome): a lista nao "pula"
 *     entre um refetch e outro com os mesmos numeros.
 *  4. O texto pesquisavel de cada modelo e normalizado UMA vez por objeto
 *     (WeakMap): a pagina chama `aplicarFiltros` sete vezes por tecla (lista +
 *     seis contagens), e renormalizar todos os campos de centenas de modelos a
 *     cada chamada custava NFD + regex em milhares de textos por tecla. Objeto
 *     novo (refetch, edicao) ganha entrada nova; o velho sai com o GC.
 */

import type { CompanyId } from '../../types';
import { taxaPagamentoBps } from './desempenho';
import type { MessageTemplate, TemplateAudience, TemplateCategory, TemplateSector, TemplateStatus, TemplateTab } from './types';
import { PUBLICO_LABEL, SETOR_LABEL, abaDoCanal, normalizarBusca, type ColunaOrdem } from './vocabulario';

/** Valores reservados do seletor de empresa; ids reais nunca colidem (comecam com `SIMULADO-` ou sao UUID). */
export const ESCOPO_TODAS = 'todas';
export const ESCOPO_GLOBAIS = 'globais';

export type EscopoEmpresa = typeof ESCOPO_TODAS | typeof ESCOPO_GLOBAIS | CompanyId;

export type FiltroUso = 'todos' | 'em_uso' | 'sem_uso';

export interface FiltrosLocais {
  readonly tab: TemplateTab;
  readonly busca: string;
  readonly categoria: TemplateCategory | 'todas';
  readonly setor: TemplateSector | 'todos';
  readonly status: TemplateStatus | 'todos';
  readonly publico: TemplateAudience | 'todos';
  readonly uso: FiltroUso;
  readonly escopo: EscopoEmpresa;
}

export const FILTROS_INICIAIS: FiltrosLocais = {
  tab: 'whatsapp',
  busca: '',
  categoria: 'todas',
  setor: 'todos',
  status: 'todos',
  publico: 'todos',
  uso: 'todos',
  escopo: ESCOPO_TODAS,
};

/** Filtros "finos" (tudo menos aba e empresa): o que "Limpar filtros" zera. */
export const FILTROS_FINOS_ZERADOS: Pick<FiltrosLocais, 'busca' | 'categoria' | 'setor' | 'status' | 'publico' | 'uso'> = {
  busca: '',
  categoria: 'todas',
  setor: 'todos',
  status: 'todos',
  publico: 'todos',
  uso: 'todos',
};

export function contarFiltrosFinos(f: FiltrosLocais): number {
  return [f.busca.trim() !== '', f.categoria !== 'todas', f.setor !== 'todos', f.status !== 'todos', f.publico !== 'todos', f.uso !== 'todos'].filter(Boolean).length;
}

/** `companyId` que viaja na query: so quando o escopo e UMA empresa. */
export function companyIdDoEscopo(escopo: EscopoEmpresa): CompanyId | null {
  return escopo === ESCOPO_TODAS || escopo === ESCOPO_GLOBAIS ? null : escopo;
}

/** Uma empresa ve os globais MAIS os dela; "so globais" ve os de `companyId: null`. */
export function pertenceAoEscopo(template: MessageTemplate, escopo: EscopoEmpresa): boolean {
  if (escopo === ESCOPO_TODAS) {
    return true;
  }
  if (escopo === ESCOPO_GLOBAIS) {
    return template.companyId === null;
  }
  return template.companyId === null || template.companyId === escopo;
}

const INDICE_BUSCA = new WeakMap<MessageTemplate, string>();

function textoPesquisavel(template: MessageTemplate): string {
  const pronto = INDICE_BUSCA.get(template);
  if (pronto !== undefined) {
    return pronto;
  }
  const campos = [
    template.name,
    template.body,
    template.subject,
    template.preheader,
    template.footer,
    template.touch,
    PUBLICO_LABEL[template.audience],
    SETOR_LABEL[template.sector],
    template.voice?.objection,
    template.voice?.closing,
    ...template.usedIn,
    ...template.buttons.map((b) => b.label),
  ];
  // Quebra de linha como separador: o campo de busca e de uma linha so, entao o termo nunca casa "atravessando" dois campos.
  const texto = campos
    .filter((campo): campo is string => campo !== undefined && campo !== null)
    .map(normalizarBusca)
    .join('\n');
  INDICE_BUSCA.set(template, texto);
  return texto;
}

function casaComBusca(template: MessageTemplate, termo: string): boolean {
  return termo === '' || textoPesquisavel(template).includes(termo);
}

/**
 * Aplica todos os filtros menos `ignorar` (para contagem facetada). A busca e
 * normalizada UMA vez fora do laco: `normalizarBusca` roda NFD e regex, e a
 * grade chama isto para cada chip.
 */
export function aplicarFiltros(lista: readonly MessageTemplate[], filtros: FiltrosLocais, ignorar: keyof FiltrosLocais | null = null): readonly MessageTemplate[] {
  const termo = normalizarBusca(filtros.busca);
  return lista.filter((t) => {
    if (ignorar !== 'escopo' && !pertenceAoEscopo(t, filtros.escopo)) {
      return false;
    }
    if (ignorar !== 'tab' && abaDoCanal(t.channel) !== filtros.tab) {
      return false;
    }
    if (ignorar !== 'categoria' && filtros.categoria !== 'todas' && t.category !== filtros.categoria) {
      return false;
    }
    if (ignorar !== 'setor' && filtros.setor !== 'todos' && t.sector !== filtros.setor) {
      return false;
    }
    if (ignorar !== 'status' && filtros.status !== 'todos' && t.status !== filtros.status) {
      return false;
    }
    if (ignorar !== 'publico' && filtros.publico !== 'todos' && t.audience !== filtros.publico) {
      return false;
    }
    if (ignorar !== 'uso' && filtros.uso !== 'todos' && (filtros.uso === 'em_uso') !== t.usedIn.length > 0) {
      return false;
    }
    if (ignorar !== 'busca' && !casaComBusca(t, termo)) {
      return false;
    }
    return true;
  });
}

/**
 * Contagem facetada de TODOS os valores de um filtro numa passada: aplica os
 * outros filtros uma vez e conta por valor. Com 5 linhas de chips e centenas
 * de modelos, e 5 passadas em vez de ~20.
 */
export function contarPorValor<K extends 'categoria' | 'setor' | 'status' | 'publico' | 'tab'>(
  lista: readonly MessageTemplate[],
  filtros: FiltrosLocais,
  chave: K,
): { readonly total: number; readonly por: (valor: string) => number } {
  const base = aplicarFiltros(lista, filtros, chave);
  const contagem = new Map<string, number>();
  for (const t of base) {
    const valor = chave === 'categoria' ? t.category : chave === 'setor' ? t.sector : chave === 'status' ? t.status : chave === 'publico' ? t.audience : abaDoCanal(t.channel);
    contagem.set(valor, (contagem.get(valor) ?? 0) + 1);
  }
  return { total: base.length, por: (valor) => contagem.get(valor) ?? 0 };
}

export function contarUso(lista: readonly MessageTemplate[], filtros: FiltrosLocais): Readonly<Record<FiltroUso, number>> {
  const base = aplicarFiltros(lista, filtros, 'uso');
  const emUso = base.filter((t) => t.usedIn.length > 0).length;
  return { todos: base.length, em_uso: emUso, sem_uso: base.length - emUso };
}

// -----------------------------------------------------------------------------
// Ordenacao
// -----------------------------------------------------------------------------

export interface Ordem {
  readonly coluna: ColunaOrdem;
  readonly desc: boolean;
}

export const ORDEM_INICIAL: Ordem = { coluna: 'pagaram', desc: true };

/** Sentido natural de cada coluna ao ser escolhida: numero comeca do maior; nome, de A. */
export function ordemNatural(coluna: ColunaOrdem): Ordem {
  return { coluna, desc: coluna !== 'nome' };
}

function valorDe(t: MessageTemplate, coluna: ColunaOrdem): number | string {
  switch (coluna) {
    case 'atualizado':
      return t.updatedAt;
    case 'nome':
      return t.name;
    case 'enviados':
      return t.performance.sent;
    case 'respostas':
      return t.performance.replied;
    case 'pagaram':
      return t.performance.paid;
    case 'taxa_pagamento':
      return taxaPagamentoBps(t.performance) ?? -1;
    case 'recuperado':
      return t.performance.recoveredCents;
  }
}

export function ordenar(lista: readonly MessageTemplate[], ordem: Ordem): readonly MessageTemplate[] {
  const sinal = ordem.desc ? -1 : 1;
  return [...lista].sort((a, b) => {
    const va = valorDe(a, ordem.coluna);
    const vb = valorDe(b, ordem.coluna);
    const comparacao = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return comparacao !== 0 ? comparacao * sinal : a.name.localeCompare(b.name);
  });
}
